import React, { useRef, useState } from 'react';

import { useEventCallback } from '@shared/utils/hooks/useEventCallback';

import { useAutoLoadComplete } from '../hooks/useAutoLoadComplete';
import { useLoadEndEffect } from '../hooks/useLoadEndEffect';
import { useLoadStartEffect } from '../hooks/useLoadStartEffect';
import { useLoadTimeoutEffect } from '../hooks/useLoadTimeoutEffect';
import type { ComponentHealthState, ComponentInfo } from '../types';
import { StateType } from '../types';
import type { InternalContextValue } from '../types';
import { ErrorBoundary } from './ErrorBoundary';
import { generateLoadId } from './generateLoadId';
import { useComponentLog } from '../hooks/useComponentLog';
import { COMPONENT_LOADING_TIMEOUT_MS, COMPONENT_SLOW_LOG_TIMEOUT_MS } from '../constants';

export interface ComponentHealthWrapperProps {
  readonly name: string;
  readonly internalContext: React.Context<InternalContextValue | undefined>;
  readonly children: React.ReactNode;
  readonly errorComponent?: React.ComponentType<{}>;
  readonly forceErrorUIOnTimeout?: boolean; // Force to show error component on timeout
  readonly timeout?: number; // component health timeout in ms
  readonly slowLogTimeout?: number; // component health slow timeout in ms
}

export const ComponentHealthWrapper: React.FC<ComponentHealthWrapperProps> = ({
  name,
  internalContext: InternalContext,
  children,
  errorComponent: ErrorComponent,
  forceErrorUIOnTimeout,
  timeout = COMPONENT_LOADING_TIMEOUT_MS,
  slowLogTimeout = COMPONENT_SLOW_LOG_TIMEOUT_MS,
}) => {
  const errorBoundaryRef = useRef<ErrorBoundary>(null);
  const componentInfoRef = useRef<ComponentInfo>({
    componentName: name,
    loadId: generateLoadId(),
    loadStartTime: Date.now(),
  });
  const [componentHealthState, setComponentHealthState] = useState<ComponentHealthState>({
    type: StateType.Loading,
  });

  useLoadStartEffect(componentInfoRef);
  useLoadEndEffect(componentInfoRef, componentHealthState);
  const { onComponentLog } = useComponentLog(componentInfoRef, componentHealthState);

  const onLoadComplete = useEventCallback(() => {
    setComponentHealthState(prevState => {
      if (prevState.type === 'loading') {
        return {
          type: StateType.Complete,
        };
      }

      return prevState;
    });
  });
  const onLoadFailure = useEventCallback((reason: unknown, context?: object, forceErrorUI?: boolean) => {
    setComponentHealthState({
      type: StateType.Failure,
      reason,
      context: {
        ...context,
        // don't permit overriding component name
        componentName: componentInfoRef.current.componentName,
      },
    });
    // Manually trigger error UI
    if (forceErrorUI) {
      errorBoundaryRef.current?.forceErrorUI();
    }
  });
  const onRegisterWithProvider = useAutoLoadComplete(onLoadComplete);

  useLoadTimeoutEffect(
    componentHealthState,
    () => {
      onLoadFailure('timeout', undefined, forceErrorUIOnTimeout);
    },
    timeout,
  );

  useLoadTimeoutEffect(
    componentHealthState,
    () => {
      onComponentLog('slow', { duration: slowLogTimeout });
    },
    slowLogTimeout,
  );

  return (
    <ErrorBoundary
      ref={errorBoundaryRef}
      onCaughtError={onLoadFailure}
      errorComponent={ErrorComponent}
    >
      <InternalContext.Provider value={{ onLoadComplete, onLoadFailure, onRegisterWithProvider }}>{children}</InternalContext.Provider>
    </ErrorBoundary>
  );
};
